import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText as MuiListItemText,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Edit,
  Delete,
  MoreVert,
  Person,
  Shield,
  CheckCircle,
  Cancel,
  AdminPanelSettings,
  Search,
  Add,
  Block,
  CheckCircleOutline,
} from '@mui/icons-material';
import {
  useSuperadminUsers,
  useDeleteSuperadminUser,
  useCreateSuperadminUser,
  useUpdateSuperadminUser,
  useToggleSuperadminUserStatus,
  useSuperadminRoles,
  useSuperadminPermissions,
} from '../../hooks';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useQueryClient } from '@tanstack/react-query';
import { colors, transitions, radius } from '../../theme/theme';

export const UsersPage = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirmation: '',
    roles: [] as number[],
    permissions: [] as number[],
  });

  const { users, isLoading, error } = useSuperadminUsers(page + 1);
  const { roles: availableRoles } = useSuperadminRoles();
  const { permissions: availablePermissions } = useSuperadminPermissions();
  const deleteUser = useDeleteSuperadminUser();
  const createUser = useCreateSuperadminUser();
  const updateUser = useUpdateSuperadminUser();
  const toggleStatus = useToggleSuperadminUserStatus();

  const filteredUsers = users?.data?.filter((user: any) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.user_type?.toLowerCase().includes(search) ||
      user.roles?.some((role: any) => role.name?.toLowerCase().includes(search))
    );
  }) || [];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    if (window.confirm(`Are you sure you want to delete ${selectedUser.name}?`)) {
      try {
        await deleteUser.mutateAsync(selectedUser.id);
        showSnackbar('User deleted successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-users'] });
        handleMenuClose();
      } catch (error) {
        showSnackbar('Failed to delete user', 'error');
      }
    }
  };

  const handleAddUser = () => {
    setFormData({
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      password: '',
      password_confirmation: '',
      roles: [],
      permissions: [],
    });
    setAddDialogOpen(true);
  };

  const handleEditUser = () => {
    if (!selectedUser) return;
    setFormData({
      first_name: selectedUser.first_name || '',
      middle_name: selectedUser.middle_name || '',
      last_name: selectedUser.last_name || '',
      email: selectedUser.email || '',
      password: '',
      password_confirmation: '',
      roles: selectedUser.roles?.map((r: any) => r.id) || [],
      permissions: selectedUser.permissions?.map((p: any) => p.id) || [],
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleToggleStatus = async () => {
    if (!selectedUser) return;

    try {
      await toggleStatus.mutateAsync(selectedUser.id);
      const newStatus = selectedUser.status === 'inactive' ? 'active' : 'inactive';
      showSnackbar(`User ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`, 'success');
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-users'] });
      handleMenuClose();
    } catch (error) {
      showSnackbar('Failed to toggle user status', 'error');
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitAdd = async () => {
    try {
      await createUser.mutateAsync(formData);
      showSnackbar('User created successfully', 'success');
      setAddDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-users'] });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to create user';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleSubmitEdit = async () => {
    if (!selectedUser) return;

    try {
      const updateData: any = {
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        email: formData.email,
      };

      // Only include password if it's provided
      if (formData.password) {
        updateData.password = formData.password;
        updateData.password_confirmation = formData.password_confirmation;
      }

      await updateUser.mutateAsync({ id: selectedUser.id, data: updateData });
      showSnackbar('User updated successfully', 'success');
      setEditDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'superadmin-users'] });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to update user';
      showSnackbar(errorMessage, 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdminPanelSettings sx={{ fontSize: 18, color: colors.primary.main }} />
          <Box>
            <Box sx={{ fontWeight: 600 }}>{params.value}</Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[600] }}>{params.row.email}</Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'user_type',
      headerName: 'User Type',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          color="primary"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'roles',
      headerName: 'Roles',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {params.value && params.value.length > 0 ? (
            params.value.map((role: any) => (
              <Chip
                key={role.id}
                icon={<Shield sx={{ fontSize: 14 }} />}
                label={role.name}
                size="small"
                color="secondary"
                sx={{ fontSize: '0.75rem' }}
              />
            ))
          ) : (
            <Chip label="No roles" size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const isActive = params.value !== 'inactive';
        return (
          <Chip
            icon={isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueGetter: (value) => (value ? new Date(value).toLocaleDateString() : 'N/A'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Superadmin Users"
          subtitle="Manage platform administrators"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'User Management' },
            { label: 'Superadmin Users' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddUser}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                transition: transitions.base,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add User
            </Button>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load superadmin users. Please try again.
          </Alert>
        )}

        {/* Search Bar */}
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Search by name, email, user type, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: colors.grey[400] }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '&:hover fieldset': {
                  borderColor: colors.primary.main,
                },
              },
            }}
          />
        </Box>

        {/* Summary Cards */}
        {!isLoading && users && (
          <Grid container spacing={2.5} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.primary,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.primary.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total Superadmins</Box>
                    <AdminPanelSettings sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {users.meta?.total?.toLocaleString() || users.data?.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Platform administrators</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.success,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.success.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Active Users</Box>
                    <CheckCircle sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {users.data?.filter((u: any) => u.status !== 'inactive').length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Currently active</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.secondary.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>With Roles</Box>
                    <Shield sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {users.data?.filter((u: any) => u.roles && u.roles.length > 0).length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Users with assigned roles</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.info,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.info.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Showing</Box>
                    <Person sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>{users.data?.length || 0}</Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>On current page</Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'visible',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: colors.shadows.base,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={filteredUsers}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ page, pageSize }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  rowCount={users?.meta?.total || users?.data?.length || 0}
                  paginationMode={users?.meta?.total ? "server" : "client"}
                  disableRowSelectionOnClick
                  autoHeight
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  sx={{
                    border: 'none',
                    borderRadius: 2,
                    '& .MuiDataGrid-main': {
                      borderRadius: 2,
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '16px 20px',
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      borderTopLeftRadius: '16px',
                      borderTopRightRadius: '16px',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-columnHeader:first-of-type': {
                      borderTopLeftRadius: '16px',
                    },
                    '& .MuiDataGrid-columnHeader:last-of-type': {
                      borderTopRightRadius: '16px',
                    },
                    '& .MuiDataGrid-row': {
                      transition: transitions.fast,
                      '&:hover': {
                        backgroundColor: `${colors.primary.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                      borderBottomLeftRadius: '16px',
                      borderBottomRightRadius: '16px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleEditUser}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit User</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleToggleStatus}>
            <ListItemIcon>
              {selectedUser?.status === 'inactive' ? (
                <CheckCircleOutline fontSize="small" sx={{ color: colors.success.main }} />
              ) : (
                <Block fontSize="small" sx={{ color: colors.warning.main }} />
              )}
            </ListItemIcon>
            <ListItemText>
              {selectedUser?.status === 'inactive' ? 'Activate User' : 'Suspend User'}
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: colors.error.main }}>
            <ListItemIcon>
              <Delete fontSize="small" sx={{ color: colors.error.main }} />
            </ListItemIcon>
            <ListItemText>Delete User</ListItemText>
          </MenuItem>
        </Menu>

        {/* Add User Dialog */}
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Superadmin User</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Middle Name"
                value={formData.middle_name}
                onChange={(e) => handleFormChange('middle_name', e.target.value)}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleFormChange('last_name', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Confirm Password"
                type="password"
                value={formData.password_confirmation}
                onChange={(e) => handleFormChange('password_confirmation', e.target.value)}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>Roles</InputLabel>
                <Select
                  multiple
                  value={formData.roles}
                  onChange={(e) => handleFormChange('roles', e.target.value)}
                  input={<OutlinedInput label="Roles" />}
                  renderValue={(selected) =>
                    (selected as number[])
                      .map((id) => Array.isArray(availableRoles) ? availableRoles.find((r: any) => r.id === id)?.name : undefined)
                      .filter(Boolean)
                      .join(', ')
                  }
                >
                  {Array.isArray(availableRoles) && availableRoles.map((role: any) => (
                    <MenuItem key={role.id} value={role.id}>
                      <Checkbox checked={formData.roles.indexOf(role.id) > -1} />
                      <MuiListItemText primary={role.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Permissions</InputLabel>
                <Select
                  multiple
                  value={formData.permissions}
                  onChange={(e) => handleFormChange('permissions', e.target.value)}
                  input={<OutlinedInput label="Permissions" />}
                  renderValue={(selected) =>
                    (selected as number[])
                      .map((id) => Array.isArray(availablePermissions) ? availablePermissions.find((p: any) => p.id === id)?.name : undefined)
                      .filter(Boolean)
                      .join(', ')
                  }
                >
                  {Array.isArray(availablePermissions) && availablePermissions.map((permission: any) => (
                    <MenuItem key={permission.id} value={permission.id}>
                      <Checkbox checked={formData.permissions.indexOf(permission.id) > -1} />
                      <MuiListItemText primary={permission.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmitAdd}
              variant="contained"
              disabled={createUser.isPending}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                },
              }}
            >
              {createUser.isPending ? 'Creating...' : 'Create User'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Superadmin User</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Middle Name"
                value={formData.middle_name}
                onChange={(e) => handleFormChange('middle_name', e.target.value)}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleFormChange('last_name', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="New Password (leave blank to keep current)"
                type="password"
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                fullWidth
              />
              <TextField
                label="Confirm New Password"
                type="password"
                value={formData.password_confirmation}
                onChange={(e) => handleFormChange('password_confirmation', e.target.value)}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Roles</InputLabel>
                <Select
                  multiple
                  value={formData.roles}
                  onChange={(e) => handleFormChange('roles', e.target.value)}
                  input={<OutlinedInput label="Roles" />}
                  renderValue={(selected) =>
                    (selected as number[])
                      .map((id) => Array.isArray(availableRoles) ? availableRoles.find((r: any) => r.id === id)?.name : undefined)
                      .filter(Boolean)
                      .join(', ')
                  }
                >
                  {Array.isArray(availableRoles) && availableRoles.map((role: any) => (
                    <MenuItem key={role.id} value={role.id}>
                      <Checkbox checked={formData.roles.indexOf(role.id) > -1} />
                      <MuiListItemText primary={role.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Permissions</InputLabel>
                <Select
                  multiple
                  value={formData.permissions}
                  onChange={(e) => handleFormChange('permissions', e.target.value)}
                  input={<OutlinedInput label="Permissions" />}
                  renderValue={(selected) =>
                    (selected as number[])
                      .map((id) => Array.isArray(availablePermissions) ? availablePermissions.find((p: any) => p.id === id)?.name : undefined)
                      .filter(Boolean)
                      .join(', ')
                  }
                >
                  {Array.isArray(availablePermissions) && availablePermissions.map((permission: any) => (
                    <MenuItem key={permission.id} value={permission.id}>
                      <Checkbox checked={formData.permissions.indexOf(permission.id) > -1} />
                      <MuiListItemText primary={permission.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmitEdit}
              variant="contained"
              disabled={updateUser.isPending}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                },
              }}
            >
              {updateUser.isPending ? 'Updating...' : 'Update User'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
